/**
 * DataManager - Adapter over storageManager
 * Same API surface, but delegates all storage to storageManager (IndexedDB v1)
 * and localStorage. Eliminates the v2 IndexedDB conflict and death loop.
 *
 * @version 3.0.0
 */

import storageManager from './storageManager.js';
import shadowBackup from './shadowBackup.js';
import opfsManager from './opfsManager.js';

class DataManager {
  constructor() {
    this.initialized = false;
    this.cache = {
      userSettings: null,
      activeTrip: null,
      trips: null
    };
    this._shadowTimer = null;
    this._autoSaveTimer = null;
  }

  // Called when an external writer (storageES) saved the active trip directly to
  // storageManager, bypassing this adapter. Most of the app writes that way, so
  // without this the .trip file / shadow / OPFS backups would never refresh after
  // the initial trip setup. Keeps all backup layers in sync. Fire-and-forget.
  async notifyActiveTripSaved() {
    this.clearCache();
    this._scheduleShadowSave();
    const activeId = storageManager.getActiveTripId();
    if (!activeId) return;
    this._triggerAutoSave(activeId);
    try {
      const trip = await storageManager.getTrip(activeId);
      if (trip) opfsManager.saveTrip(trip).catch(() => {});
    } catch (_) { /* OPFS best-effort */ }
  }

  // Debounced shadow backup — fires 3s after the last trip write
  _scheduleShadowSave() {
    clearTimeout(this._shadowTimer);
    this._shadowTimer = setTimeout(async () => {
      try {
        const trips = await this.getAllTrips();
        shadowBackup.save(trips);
      } catch (e) { /* silent — shadow backup must never break the main flow */ }
    }, 3000);
  }

  // ==========================================
  // INITIALIZATION
  // ==========================================

  async initialize() {
    if (this.initialized) return;
    await storageManager.initialize();
    this.initialized = true;
    console.log('DataManager: Initialized (adapter over storageManager)');

    // If IndexedDB is empty, try to recover from OPFS before anything else
    const existing = await storageManager.getAllTrips();
    if (existing.length === 0) {
      await this._recoverFromOPFS();
    }

    // Shadow backup on every start (captures current state)
    this._scheduleShadowSave();

    // Restore FSA file handles (fire-and-forget, non-blocking)
    import('./tripFileManager.js').then(({ default: tfm }) => {
      tfm.restoreFileHandles().catch(() => {});
    }).catch(() => {});
  }

  // ── OPFS recovery ──────────────────────────────────────────────────────────

  async _recoverFromOPFS() {
    try {
      const trips = await opfsManager.getAllTrips();
      if (trips.length === 0) return;

      console.log(`[DataManager] Recovering ${trips.length} trip(s) from OPFS...`);
      for (const trip of trips) {
        await storageManager.saveTrip(trip);
      }

      const active = trips.find(t => t.status === 'active');
      storageManager.saveTripsIndex({
        activeTrip:   active?.tripId || trips[0]?.tripId || null,
        tripIds:      trips.map(t => t.tripId),
        lastModified: new Date().toISOString(),
      });

      console.log('[DataManager] OPFS recovery complete');
      window.dispatchEvent(new CustomEvent('wayfare-opfs-recovered', {
        detail: { tripCount: trips.length },
      }));
    } catch (e) {
      console.warn('[DataManager] OPFS recovery failed:', e.message);
    }
  }

  clearCache() {
    this.cache = { userSettings: null, activeTrip: null, trips: null };
  }

  // ==========================================
  // USER SETTINGS (localStorage)
  // ==========================================

  async getUserSettings() {
    if (this.cache.userSettings) return this.cache.userSettings;

    try {
      const raw = localStorage.getItem('userSettings');
      if (raw) {
        const settings = JSON.parse(raw);
        this.cache.userSettings = settings;
        return settings;
      }
    } catch (e) {
      console.warn('[DataManager] Error reading userSettings:', e);
    }

    const defaults = this._getDefaultUserSettings();
    this.cache.userSettings = defaults;
    return defaults;
  }

  async saveUserSettings(settings) {
    const toSave = { ...settings, lastModified: new Date().toISOString() };
    try {
      localStorage.setItem('userSettings', JSON.stringify(toSave));
      this.cache.userSettings = toSave;
    } catch (e) {
      console.error('[DataManager] Error saving userSettings:', e);
    }
  }

  _getDefaultUserSettings() {
    return {
      userName: 'TRAVELER',
      location: 'CITY',
      country: 'COUNTRY',
      theme: 'auto',
      language: 'en',
      activeTrip: null,
      lastModified: new Date().toISOString()
    };
  }

  // ==========================================
  // TRIP MANAGEMENT
  // ==========================================

  async getActiveTrip() {
    if (this.cache.activeTrip) return this.cache.activeTrip;
    await this.initialize();
    const trip = await storageManager.getActiveTrip();
    this.cache.activeTrip = trip;
    return trip;
  }

  async getAllTrips() {
    if (this.cache.trips) return this.cache.trips;
    await this.initialize();
    const trips = await storageManager.getAllTrips();
    trips.sort((a, b) => {
      const dateA = new Date(a.dates?.startDate || 0);
      const dateB = new Date(b.dates?.startDate || 0);
      return dateB - dateA;
    });
    this.cache.trips = trips;
    return trips;
  }

  async getTrip(tripId) {
    await this.initialize();
    return (await storageManager.getTrip(tripId)) || null;
  }

  async createTrip(tripData, setAsActive = true) {
    await this.initialize();

    const tripId = tripData.tripId || storageManager.generateTripId();

    const trip = {
      tripId,
      ownerEmail: tripData.ownerEmail || storageManager.getOwner() || null, // scope to current account
      destination: tripData.destination || { city: 'Unknown', country: 'Unknown' },
      dates: tripData.dates || {
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        currentDay: 1,
        totalDays: 1
      },
      status: setAsActive ? 'active' : 'completed',
      coverImage: tripData.coverImage || null,
      stats: {
        totalMemories: 0,
        totalPhotos: 0,
        totalReflections: 0,
        completedChallenges: 0
      },
      photos: [],
      notes: tripData.notes || [],
      challenges: tripData.challenges || [],
      reflections: tripData.reflections || [],
      luggage: tripData.luggage || [],
      groceries: tripData.groceries || [],
      gifts: tripData.gifts || [],
      lodgings: tripData.lodgings || [],
      privateDocuments: tripData.privateDocuments || [],
      // Sprint 1 — proximity reveal / serendipity (per-trip, additive)
      revealedPois: tripData.revealedPois || [],
      surfacedPois: tripData.surfacedPois || [],
      dayClosures: tripData.dayClosures || {},
      // Sprint 2 — sealed book + open threads (additive)
      sealedAt: tripData.sealedAt || null,
      openThreads: tripData.openThreads || [],
      travelInfo: tripData.travelInfo || {
        flights: [],
        cars: [],
        trains: [],
        buses: []
      },
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    await storageManager.saveTrip(trip);

    // Update trips_index
    const index = storageManager.getTripsIndex();
    if (!index.tripIds.includes(tripId)) {
      index.tripIds.push(tripId);
    }

    if (setAsActive) {
      // Archive current active trip
      const currentActive = await storageManager.getActiveTrip();
      if (currentActive) {
        await storageManager.updateTripStatus(currentActive.tripId, 'completed');
      }
      index.activeTrip = tripId;

      // Update user settings
      const settings = await this.getUserSettings();
      settings.activeTrip = tripId;
      settings.location = trip.destination.city;
      settings.country = trip.destination.country;
      await this.saveUserSettings(settings);
    }

    index.lastModified = new Date().toISOString();
    storageManager.saveTripsIndex(index);

    this.clearCache();
    this._scheduleShadowSave();
    opfsManager.saveTrip(trip).catch(() => {});
    return trip;
  }

  async updateTrip(tripId, updates) {
    await this.initialize();

    const trip = await storageManager.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    const updatedTrip = {
      ...trip,
      ...updates,
      tripId, // Ensure ID doesn't change
      lastModified: new Date().toISOString()
    };

    await storageManager.saveTrip(updatedTrip);
    this.clearCache();
    this._triggerAutoSave(tripId);
    this._scheduleShadowSave();
    opfsManager.saveTrip(updatedTrip).catch(() => {});
    return updatedTrip;
  }

  async updateTripStatus(tripId, status) {
    await this.initialize();
    await storageManager.updateTripStatus(tripId, status);
    this.clearCache();
  }

  async switchActiveTrip(tripId) {
    await this.initialize();

    const trip = await storageManager.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    // Archive current active trip
    const currentActive = await storageManager.getActiveTrip();
    if (currentActive && currentActive.tripId !== tripId) {
      await storageManager.updateTripStatus(currentActive.tripId, 'completed');
    }

    // Set new trip as active
    await storageManager.updateTripStatus(tripId, 'active');

    // Update trips_index
    const index = storageManager.getTripsIndex();
    index.activeTrip = tripId;
    index.lastModified = new Date().toISOString();
    storageManager.saveTripsIndex(index);

    // Update user settings
    const settings = await this.getUserSettings();
    settings.activeTrip = tripId;
    settings.location = trip.destination.city;
    settings.country = trip.destination.country;
    await this.saveUserSettings(settings);

    this.clearCache();
    return trip;
  }

  async deleteTrip(tripId) {
    await this.initialize();

    // Delete trip and its photos from IndexedDB
    await storageManager.deleteTrip(tripId);

    // Update trips_index
    const index = storageManager.getTripsIndex();
    index.tripIds = index.tripIds.filter(id => id !== tripId);
    if (index.activeTrip === tripId) {
      index.activeTrip = null;
    }
    index.lastModified = new Date().toISOString();
    storageManager.saveTripsIndex(index);

    // Clear active trip from settings if needed
    const settings = await this.getUserSettings();
    if (settings.activeTrip === tripId) {
      settings.activeTrip = null;
      settings.location = 'CITY';
      settings.country = 'COUNTRY';
      await this.saveUserSettings(settings);
    }

    this.clearCache();
  }

  // ==========================================
  // PHOTO METHODS
  // ==========================================

  async addPhoto(tripId, photoData) {
    await this.initialize();

    const photoId = storageManager.generatePhotoId();

    const photo = {
      id: photoId,
      tripId,
      data: photoData.data,
      caption: photoData.caption || '',
      location: photoData.location || '',
      timestamp: photoData.timestamp || new Date().toISOString(),
      size: photoData.size || 0
    };

    await storageManager.savePhoto(photo);

    // Update trip stats and photo references
    const trip = await storageManager.getTrip(tripId);
    if (trip) {
      if (!trip.photos) trip.photos = [];
      trip.photos.push(photoId);
      trip.stats = trip.stats || {};
      trip.stats.totalPhotos = trip.photos.length;
      await storageManager.saveTrip(trip);
    }

    this.clearCache();
    this._triggerAutoSave(tripId);
    return photo;
  }

  async getPhotos(tripId) {
    await this.initialize();
    return await storageManager.getPhotosByTrip(tripId);
  }

  async getPhoto(photoId) {
    await this.initialize();
    return (await storageManager.getPhoto(photoId)) || null;
  }

  async deletePhoto(photoId) {
    await this.initialize();

    const photo = await storageManager.getPhoto(photoId);
    if (!photo) return;

    const tripId = photo.tripId;

    // storageManager.deletePhoto handles both photo and thumbnail
    await storageManager.deletePhoto(photoId);

    // Update trip stats
    const trip = await storageManager.getTrip(tripId);
    if (trip && trip.photos) {
      trip.photos = trip.photos.filter(id => id !== photoId);
      trip.stats = trip.stats || {};
      trip.stats.totalPhotos = trip.photos.length;
      await storageManager.saveTrip(trip);
    }

    this.clearCache();
    this._triggerAutoSave(tripId);
  }

  async saveThumbnail(photoId, thumbnailBlob) {
    await this.initialize();
    return await storageManager.saveThumbnail(photoId, thumbnailBlob);
  }

  async getThumbnail(photoId) {
    await this.initialize();
    return await storageManager.getThumbnail(photoId);
  }

  async deleteThumbnail(photoId) {
    // No-op: storageManager.deletePhoto already handles thumbnail cleanup
  }

  // ==========================================
  // NOTE METHODS
  // ==========================================

  async addNote(tripId, noteData) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    const note = {
      id: Date.now(),
      ...noteData,
      timestamp: new Date().toISOString()
    };

    if (!trip.notes) trip.notes = [];
    trip.notes.push(note);
    trip.stats = trip.stats || {};
    trip.stats.totalMemories = trip.notes.length;

    await this.updateTrip(tripId, { notes: trip.notes, stats: trip.stats });
    return trip;
  }

  async getNotes(tripId) {
    const trip = await this.getTrip(tripId);
    return trip?.notes || [];
  }

  async updateNote(tripId, noteId, updates) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    const noteIndex = trip.notes.findIndex(n => n.id === noteId);
    if (noteIndex === -1) {
      throw new Error(`Note ${noteId} not found`);
    }

    trip.notes[noteIndex] = {
      ...trip.notes[noteIndex],
      ...updates,
      lastModified: new Date().toISOString()
    };

    await this.updateTrip(tripId, { notes: trip.notes });
    return trip;
  }

  async deleteNote(tripId, noteId) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    trip.notes = trip.notes.filter(n => n.id !== noteId);
    trip.stats = trip.stats || {};
    trip.stats.totalMemories = trip.notes.length;

    await this.updateTrip(tripId, { notes: trip.notes, stats: trip.stats });
    return trip;
  }

  // ==========================================
  // REFLECTION METHODS
  // ==========================================

  async addReflection(tripId, reflectionData) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    const reflection = {
      id: Date.now(),
      ...reflectionData,
      timestamp: new Date().toISOString()
    };

    if (!trip.reflections) trip.reflections = [];
    trip.reflections.push(reflection);
    trip.stats = trip.stats || {};
    trip.stats.totalReflections = trip.reflections.length;

    await this.updateTrip(tripId, { reflections: trip.reflections, stats: trip.stats });
    return trip;
  }

  async getReflections(tripId) {
    const trip = await this.getTrip(tripId);
    return trip?.reflections || [];
  }

  // ==========================================
  // CHALLENGE METHODS
  // ==========================================

  async markChallengeComplete(tripId, challengeId) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    if (!trip.challenges) trip.challenges = [];
    if (!trip.challenges.includes(challengeId)) {
      trip.challenges.push(challengeId);
      trip.stats = trip.stats || {};
      trip.stats.completedChallenges = trip.challenges.length;
      await this.updateTrip(tripId, { challenges: trip.challenges, stats: trip.stats });
    }

    return trip;
  }

  async getCompletedChallenges(tripId) {
    const trip = await this.getTrip(tripId);
    return trip?.challenges || [];
  }

  // ==========================================
  // BUDGET METHODS (localStorage)
  // ==========================================

  async getBudget(tripId) {
    try {
      const raw = localStorage.getItem('budgetData');
      if (raw) return JSON.parse(raw);
    } catch (e) {
      console.warn('[DataManager] Error reading budgetData:', e);
    }
    return this._getDefaultBudget(tripId);
  }

  async saveBudget(tripId, budgetData) {
    try {
      localStorage.setItem('budgetData', JSON.stringify(budgetData));
    } catch (e) {
      console.error('[DataManager] Error saving budgetData:', e);
    }
    this._triggerAutoSave(tripId);
  }

  async deleteBudget(tripId) {
    try {
      localStorage.removeItem('budgetData');
    } catch (e) {
      console.warn('[DataManager] Error deleting budgetData:', e);
    }
  }

  _getDefaultBudget(tripId) {
    return {
      id: `budget_${tripId}`,
      tripId,
      total: 0,
      spent: 0,
      categories: {},
      transactions: [],
      lastModified: new Date().toISOString()
    };
  }

  // ==========================================
  // STORAGE MANAGEMENT METHODS
  // ==========================================

  async getStorageStats() {
    return await storageManager.getStorageStats();
  }

  async exportTrip(tripId) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    const photos = await this.getPhotos(tripId);
    const budget = await this.getBudget(tripId);

    return {
      version: '2.0',
      exportDate: new Date().toISOString(),
      trip,
      photoCount: photos.length,
      budget,
      photoMetadata: photos.map(p => ({
        id: p.id,
        caption: p.caption,
        location: p.location,
        timestamp: p.timestamp
      }))
    };
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  close() {
    this.initialized = false;
    this.clearCache();
  }

  async getStats() {
    const trips = await this.getAllTrips();
    const activeTrip = await this.getActiveTrip();

    let totalPhotos = 0;
    let totalNotes = 0;
    let totalReflections = 0;
    let totalChallenges = 0;

    for (const trip of trips) {
      totalPhotos += trip.photos?.length || 0;
      totalNotes += trip.notes?.length || 0;
      totalReflections += trip.reflections?.length || 0;
      totalChallenges += trip.challenges?.length || 0;
    }

    return {
      totalTrips: trips.length,
      activeTrips: trips.filter(t => t.status === 'active').length,
      completedTrips: trips.filter(t => t.status === 'completed').length,
      activeTrip: activeTrip ? activeTrip.destination.city : null,
      totalPhotos,
      totalNotes,
      totalReflections,
      totalChallenges
    };
  }

  /**
   * Trigger auto-save for a trip (uses dynamic import to avoid circular dependency)
   * @private
   */
  _triggerAutoSave(tripId) {
    // Debounced: coalesces rapid edits into a single file write (each write
    // re-serialises all photos to base64, so it's worth not doing per keystroke).
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = setTimeout(async () => {
      try {
        const { default: tripFileManager } = await import('./tripFileManager.js');
        tripFileManager.autoSave(tripId).catch(err => {
          console.debug('[DataManager] Auto-save skipped or failed:', err.message);
        });
      } catch (err) {
        // tripFileManager not available
      }
    }, 800);
  }
}

// Create singleton instance
const dataManager = new DataManager();

// Make available globally for backward compatibility
window.DataManager = dataManager;

export default dataManager;
